import { createContext, useCallback, useContext, useRef } from 'react';
import { useFrame } from '@/lib/polaris';

const DEFAULT_DURATION = 5000;

/**
 * El siguiente paso, cuando lo hay. Un aviso de «falta X» sin un botón para conseguir X deja al
 * usuario leyendo en vez de resolviendo. Polaris ya lo soporta (`Toast.action`); acá sólo se expone.
 */
export type AccionDelToast = { content: string; onAction: () => void };

type ShowToastOptions = { error?: boolean; duration?: number; action?: AccionDelToast };

type AdminToastContextValue = {
  success: (message: string, description?: string, action?: AccionDelToast) => void;
  error: (message: string, description?: string, action?: AccionDelToast) => void;
  /** Acepta `description` igual que success/error: antes no, y los call-sites que la
   *  pasaban la perdian en silencio (5 en el repo). `show()` ya la soportaba. */
  info: (message: string, description?: string, action?: AccionDelToast) => void;
};

const AdminToastContext = createContext<AdminToastContextValue | null>(null);

/**
 * Debe renderizarse DENTRO de <Frame> para tener acceso a showToast/hideToast.
 * Envuelve el contenido del admin (Outlet) para que cualquier hijo use useAdminToast().
 */
export function AdminToastProvider({ children }: { children: React.ReactNode }) {
  const { showToast, hideToast } = useFrame();
  const idSeedRef = useRef(0);
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const show = useCallback(
    (content: string, options: ShowToastOptions & { description?: string } = {}) => {
      const id = `admin-toast-${Date.now()}-${idSeedRef.current++}`;
      const duration = options.duration ?? DEFAULT_DURATION;

      const onDismiss = () => {
        const t = timeoutsRef.current.get(id);
        if (t) clearTimeout(t);
        timeoutsRef.current.delete(id);
        hideToast({ id });
      };

      showToast({
        id,
        content: options.description ? `${content}. ${options.description}` : content,
        error: options.error ?? false,
        duration,
        onDismiss,
        ...(options.action ? { action: options.action } : {}),
      });

      const t = setTimeout(onDismiss, duration);
      timeoutsRef.current.set(id, t);
    },
    [showToast, hideToast]
  );

  const value: AdminToastContextValue = {
    success: (message, description, action) => show(message, { description, action }),
    error: (message, description, action) => show(message, { error: true, description, action }),
    info: (message, description, action) => show(message, { description, action }),
  };

  return (
    <AdminToastContext.Provider value={value}>
      {children}
    </AdminToastContext.Provider>
  );
}

export function useAdminToast(): AdminToastContextValue {
  const ctx = useContext(AdminToastContext);
  if (!ctx) {
    throw new Error('useAdminToast must be used within AdminToastProvider (inside Frame).');
  }
  return ctx;
}

/**
 * Hook opcional que no lanza si se usa fuera del admin (p. ej. en login).
 * Retorna null si no hay contexto, para poder usar toast de Sonner como fallback.
 */
export function useAdminToastOptional(): AdminToastContextValue | null {
  return useContext(AdminToastContext);
}
